/**
 * Board state the screen and the canvas share: where the viewport is, and how thick
 * the pen is set.
 *
 * Zoom moves through a fixed ladder rather than a percentage step, so the teacher can
 * land on exactly 50%, 100% or 300% — the levels SRS C3 is specified in — instead of
 * creeping past them.
 */

import { create } from 'zustand';

import { DEFAULT_BACKGROUND, type PageBackground } from '@opal/board-doc';

import {
  clamp,
  clampZoom,
  DEFAULT_TARGET_WIDTH_PX,
  fitToContent,
  panByScreen,
  zoomAt,
  type BoardAccessState,
  type BoardObject,
  type EraserMode,
  type Layer,
  type PageSummary,
  type Point,
  type Rect,
  type ShapeKind,
  type StickyColor,
  type TextAlign,
  type ToolId,
  type Viewport,
} from '@opal/shared';

import type { SelectionActions } from './select-session.js';
import type { SnapOffer } from './smart-pen.js';
import type { SelectMode } from './selection.js';
import type { TextActions, TextRestyle } from './text-session.js';

import { DEFAULT_ERASER_PX, MAX_ERASER_PX, MIN_ERASER_PX } from './erase.js';
import { DEFAULT_POLYGON_SIDES, MAX_POLYGON_SIDES, MIN_POLYGON_SIDES } from './shape-geometry.js';
import { addRecentColor, DEFAULT_PEN_COLOR, inkColor } from './palette.js';
import { DEFAULT_SHAPE_CONFIDENCE } from './shape-recognise.js';
import { DEFAULT_STICKY_COLOR } from './sticky-colors.js';
import {
  BOLD_WEIGHT,
  DEFAULT_FONT_SIZE,
  MAX_FONT_SIZE,
  MIN_FONT_SIZE,
  NORMAL_WEIGHT,
} from './text-object.js';
import { INITIAL_VIEWPORT } from './viewport-state.js';

/** SRS C2 — 10% to 800%. */
export const ZOOM_LADDER: readonly number[] = [0.1, 0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8];

export interface BoardState {
  readonly viewport: Viewport;
  /** SRS C3 — what the pen aims to feel like, in screen pixels. */
  readonly targetScreenWidth: number;
  /** Supplied by the board once it exists; null before then and after it unmounts. */
  readonly contentBounds: (() => Rect | null) | null;
  /** Bumped whenever a stroke lands, so the scrollbars can re-measure the work. */
  readonly contentVersion: number;
  /** SRS C7 — the paper this page is drawn on. */
  readonly background: PageBackground;
  /** SRS C4 — what is in the teacher's hand. */
  readonly tool: ToolId;
  /** SRS C4 — which of the eraser's three ways of working is in use. */
  readonly eraserMode: EraserMode;
  /**
   * How wide the eraser is on screen, in pixels.
   *
   * Screen width rather than canvas width, exactly like the pen (SRS C3): the circle
   * under the hand stays the same size whatever the zoom, and the area of board it
   * covers is worked out from it.
   */
  readonly eraserScreenWidth: number;
  /** SRS C4 — whether the select tool draws a freehand loop or a box. */
  readonly selectMode: SelectMode;
  /** SRS C4 — which of the six shapes the shapes tool will draw. */
  readonly shapeKind: ShapeKind;
  /** How many sides a polygon has. Only used when the polygon is chosen. */
  readonly polygonSides: number;
  /**
   * What the panel can do to the selection, lent by the board once it exists.
   *
   * The board owns the selection because the canvas is what the hand touches; the
   * panel only ever asks it for things.
   */
  readonly selection: SelectionActions | null;
  /** How many strokes are selected, so the panel knows whether to offer anything. */
  readonly selectionCount: number;
  /** Whether what is selected is grouped, so the panel offers the right button. */
  readonly selectionGrouped: boolean;
  /** §16 — how many pieces the clipboard holds, so Paste is offered only when it works. */
  readonly selectionCopied: number;
  /** SRS C4 — how big new writing is, in canvas units, and how it is set. */
  readonly fontSize: number;
  readonly bold: boolean;
  readonly italic: boolean;
  readonly align: TextAlign;
  /** SRS C4 — which of the six papers a new sticky note is written on. */
  readonly stickyColor: StickyColor;
  /**
   * The box being typed into, lent by the board, and what can be done to it.
   *
   * The typing happens in a real text field over the canvas, so the screen has to
   * know which box is open and where it is. The board owns it; the screen only shows
   * it and hands back what was typed.
   */
  /** SRS C8 — which layer new work goes on: the lesson's, or the teacher's own. */
  readonly activeLayer: Layer;
  /** SRS C1 — the pages of this board, in tab order. */
  readonly pages: readonly PageSummary[];
  /** Which of them is being worked on, or null before the board has arrived. */
  readonly activePageId: string | null;
  readonly editing: BoardObject | null;
  readonly writing: TextActions | null;
  /** SRS C5 — the shape that just snapped, while the way back is still offered. */
  readonly snapUndo: SnapOffer | null;
  /** SRS C5 — whether rough shapes snap and ink is smoothed. On, until turned off. */
  readonly smartPen: boolean;
  /** SRS K3 — how sure the recogniser must be, 0 to 1. Set by an administrator. */
  readonly snapConfidence: number;
  /** The colour the next stroke will be drawn in. */
  readonly color: string;
  /** docs/06-UI-SPEC.md §6.3 — the six colours used most recently, newest first. */
  readonly recentColors: readonly string[];
  /**
   * SRS D4 — what this person may do on this board, as the server last said.
   *
   * The server enforces this on every message it receives; keeping it here as well is
   * what stops a student who may not draw from making marks that would only be thrown
   * away, and lets the screen say why.
   */
  readonly access: BoardAccessState;
  /** Declared as properties, not methods, so they can be pulled off the store safely. */
  readonly setSize: (widthPx: number, heightPx: number) => void;
  readonly setZoom: (zoom: number) => void;
  /** Puts the view exactly here. Used to follow the presenter (SRS D6). */
  readonly setViewport: (viewport: Viewport) => void;
  /** Drags the paper by a distance in screen pixels. */
  readonly panBy: (dxPx: number, dyPx: number) => void;
  /** Zooms about a point on screen, so what is under the cursor stays put. */
  readonly zoomAtPoint: (zoom: number, anchor: Point) => void;
  /** The board tells the store how to measure its own content, for Fit to Content. */
  readonly setContentBounds: (provider: (() => Rect | null) | null) => void;
  /** Frames everything drawn so far. Does nothing on an empty board. */
  readonly fitToContent: () => void;
  /** Tells the screen that the drawn area has changed. */
  readonly contentChanged: () => void;
  /** Changes the paper: its type, its spacing, or its colour. */
  readonly setBackground: (patch: Partial<PageBackground>) => void;
  /** Picks up a tool. Work already on the board is untouched. */
  readonly setTool: (tool: ToolId) => void;
  /** Chooses how the eraser works: whole strokes, an area, or one clicked object. */
  readonly setEraserMode: (mode: EraserMode) => void;
  /** Chooses how wide the eraser is, in screen pixels. */
  readonly setEraserScreenWidth: (px: number) => void;
  /** Chooses between the freehand lasso and the dragged box. */
  readonly setSelectMode: (mode: SelectMode) => void;
  /** Chooses which shape the shapes tool draws next. */
  readonly setShapeKind: (kind: ShapeKind) => void;
  /** Chooses how many sides a polygon has. */
  readonly setPolygonSides: (sides: number) => void;
  /** Chooses how big new writing is, in canvas units. */
  readonly setFontSize: (size: number) => void;
  /** SRS C4 — bold, italic and how the words sit in their box. */
  readonly setBold: (bold: boolean) => void;
  readonly setItalic: (italic: boolean) => void;
  readonly setAlign: (align: TextAlign) => void;
  /** Chooses which paper the next sticky note is on. */
  readonly setStickyColor: (color: StickyColor) => void;
  /** The board says which box is open for typing, and takes it back when it closes. */
  readonly setEditing: (object: BoardObject | null) => void;
  /** The board lends the screen the text tool, and takes it back when it unmounts. */
  readonly setWriting: (writing: TextActions | null) => void;
  /** SRS C5 — the board offers a way back from a snap, and withdraws it after 4 s. */
  readonly setSnapUndo: (offer: SnapOffer | null) => void;
  /** SRS C5 — the Smart Pen switch in the pen panel. */
  readonly setSmartPen: (on: boolean) => void;
  /** SRS K3 — the one number an administrator can turn, 0 to 1. */
  readonly setSnapConfidence: (confidence: number) => void;
  /** SRS C8 — chooses the layer new work goes on. */
  readonly setActiveLayer: (layer: Layer) => void;
  /** SRS C1 — the tab strip, told by the board what pages it has. */
  readonly setPages: (pages: readonly PageSummary[]) => void;
  readonly setActivePage: (pageId: string | null) => void;
  /** The board lends the panel its selection, and takes it back when it unmounts. */
  readonly setSelection: (selection: SelectionActions | null) => void;
  /** Tells the panel how much is selected, whether it is grouped, and how much is held. */
  readonly setSelectionState: (count: number, grouped: boolean, copied: number) => void;
  /** Chooses the ink colour, and remembers it in the recent row. */
  readonly setColor: (color: string) => void;
  /** Records what the server said this person may do. */
  readonly setAccess: (access: BoardAccessState) => void;
  readonly zoomIn: () => void;
  readonly zoomOut: () => void;
  readonly resetZoom: () => void;
  readonly setTargetScreenWidth: (px: number) => void;
}

/** The next rung up the ladder from here, or the top one. */
export function zoomAbove(zoom: number): number {
  return ZOOM_LADDER.find((rung) => rung > zoom + 0.0001) ?? zoom;
}

/** The next rung down, or the bottom one. */
export function zoomBelow(zoom: number): number {
  return [...ZOOM_LADDER].reverse().find((rung) => rung < zoom - 0.0001) ?? zoom;
}

/** How the store hands its own setter and getter to the action groups below. */
type Setter = (patch: Partial<BoardState>) => void;
type Getter = () => BoardState;

/**
 * Everything the teacher can choose about how the next mark will look: the tool, the
 * colour, how thick it feels, and the paper under it. None of it ever reaches back
 * into work already drawn.
 */
function penActions(
  set: Setter,
  get: Getter,
): Pick<
  BoardState,
  | 'setBackground'
  | 'setTool'
  | 'setColor'
  | 'setTargetScreenWidth'
  | 'setAccess'
  | 'setEraserMode'
  | 'setEraserScreenWidth'
  | 'setSelectMode'
  | 'setShapeKind'
  | 'setPolygonSides'
> {
  return {
    setBackground: (patch) => {
      set({ background: { ...get().background, ...patch } });
    },

    setTool: (tool) => {
      set({ tool });
    },

    setColor: (color) => {
      set({ color, recentColors: addRecentColor(get().recentColors, color) });
      // Words on the board carry a real colour, never the name of one: what is drawn
      // has to be readable by everybody else's browser too (docs/05-DATA-MODEL.md).
      restyle(get, 'text', { color: inkColor(color) });
    },

    setTargetScreenWidth: (px) => {
      set({ targetScreenWidth: px });
    },

    setAccess: (access) => {
      set({ access });
    },

    setEraserMode: (eraserMode) => {
      set({ eraserMode });
    },

    setEraserScreenWidth: (px) => {
      set({ eraserScreenWidth: clamp(px, MIN_ERASER_PX, MAX_ERASER_PX) });
    },

    setSelectMode: (selectMode) => {
      set({ selectMode });
    },

    setShapeKind: (shapeKind) => {
      set({ shapeKind });
    },

    setPolygonSides: (sides) => {
      set({ polygonSides: clamp(Math.round(sides), MIN_POLYGON_SIDES, MAX_POLYGON_SIDES) });
    },
  };
}

/**
 * Changes the box being typed into right now, if there is one and it is this kind.
 *
 * A box that is open belongs to the panel while it is open: making the words bold
 * changes them where they stand. Words already finished are never touched, the same
 * way a thicker pen never thickens a line that is already drawn.
 */
function restyle(get: Getter, kind: 'text' | 'sticky', patch: TextRestyle): void {
  const state = get();
  if (state.editing?.kind !== kind) return;
  state.writing?.restyle(patch);
}

/**
 * The text tool and the sticky notes, as the panel sets them.
 *
 * Each of these sets up the next box, and changes the one open now if it is of the
 * kind the control belongs to.
 */
function textStyleActions(
  set: Setter,
  get: Getter,
): Pick<BoardState, 'setFontSize' | 'setBold' | 'setItalic' | 'setAlign' | 'setStickyColor'> {
  return {
    setFontSize: (size) => {
      const fontSize = clamp(Math.round(size), MIN_FONT_SIZE, MAX_FONT_SIZE);
      set({ fontSize });
      restyle(get, 'text', { fontSize });
    },

    setBold: (bold) => {
      set({ bold });
      restyle(get, 'text', { fontWeight: bold ? BOLD_WEIGHT : NORMAL_WEIGHT });
    },

    setItalic: (italic) => {
      set({ italic });
      restyle(get, 'text', { italic });
    },

    setAlign: (align) => {
      set({ align });
      restyle(get, 'text', { align });
    },

    setStickyColor: (stickyColor) => {
      set({ stickyColor });
      restyle(get, 'sticky', { color: stickyColor });
    },
  };
}

/** The text field over the canvas, and the strip of pages along the bottom. */
function editingActions(
  set: Setter,
): Pick<
  BoardState,
  | 'setEditing'
  | 'setWriting'
  | 'setSnapUndo'
  | 'setSmartPen'
  | 'setSnapConfidence'
  | 'setPages'
  | 'setActivePage'
  | 'setActiveLayer'
> {
  return {
    setActiveLayer: (activeLayer) => {
      set({ activeLayer });
    },

    setEditing: (editing) => {
      set({ editing });
    },

    setWriting: (writing) => {
      set({ writing, editing: null });
    },

    setSnapUndo: (snapUndo) => {
      set({ snapUndo });
    },

    setSmartPen: (smartPen) => {
      set({ smartPen });
    },

    setSnapConfidence: (snapConfidence) => {
      set({ snapConfidence: clamp(snapConfidence, 0, 1) });
    },

    setPages: (pages) => {
      set({ pages });
    },

    setActivePage: (activePageId) => {
      set({ activePageId });
    },
  };
}

/**
 * How much has been drawn, and framing it.
 *
 * The board itself is the only thing that can measure its own content, so it lends
 * the store a way to ask; everything that needs the answer — Fit to Content, the
 * scrollbars — goes through here.
 */
function contentActions(
  set: Setter,
  get: Getter,
): Pick<BoardState, 'setContentBounds' | 'contentChanged' | 'fitToContent'> {
  return {
    setContentBounds: (provider) => {
      set({ contentBounds: provider, contentVersion: get().contentVersion + 1 });
    },

    contentChanged: () => {
      set({ contentVersion: get().contentVersion + 1 });
    },

    fitToContent: () => {
      const bounds = get().contentBounds?.();
      if (!bounds) return;
      set({ viewport: fitToContent(get().viewport, bounds) });
    },
  };
}

/**
 * The selection, as the panel sees it.
 *
 * The board owns what is chosen, because the canvas is what the hand touches. It
 * lends the panel a way to act on it, and tells it how much is chosen; the panel
 * never reaches into the board itself.
 */
function selectionActions(
  set: Setter,
  get: Getter,
): Pick<BoardState, 'setSelection' | 'setSelectionState'> {
  return {
    setSelection: (selection) => {
      set({ selection, selectionCount: 0, selectionGrouped: false, selectionCopied: 0 });
    },

    setSelectionState: (selectionCount, selectionGrouped, selectionCopied) => {
      const current = get();
      if (
        current.selectionCount === selectionCount &&
        current.selectionGrouped === selectionGrouped &&
        current.selectionCopied === selectionCopied
      ) {
        return;
      }
      set({ selectionCount, selectionGrouped, selectionCopied });
    },
  };
}

/** Where the board is looking: the size of the window onto it, the pan and the zoom. */
function viewportActions(
  set: Setter,
  get: Getter,
): Pick<
  BoardState,
  | 'setSize'
  | 'setZoom'
  | 'setViewport'
  | 'panBy'
  | 'zoomAtPoint'
  | 'zoomIn'
  | 'zoomOut'
  | 'resetZoom'
> {
  return {
    setSize: (widthPx, heightPx) => {
      const viewport = get().viewport;
      if (viewport.widthPx === widthPx && viewport.heightPx === heightPx) return;
      set({ viewport: { ...viewport, widthPx, heightPx } });
    },

    setZoom: (zoom) => {
      set({ viewport: zoomCentred(get().viewport, zoom) });
    },

    setViewport: (viewport) => {
      set({ viewport });
    },

    panBy: (dxPx, dyPx) => {
      if (dxPx === 0 && dyPx === 0) return;
      set({ viewport: panByScreen(get().viewport, dxPx, dyPx) });
    },

    zoomAtPoint: (zoom, anchor) => {
      set({ viewport: zoomAt(get().viewport, clampZoom(zoom), anchor) });
    },

    zoomIn: () => {
      get().setZoom(zoomAbove(get().viewport.zoom));
    },

    zoomOut: () => {
      get().setZoom(zoomBelow(get().viewport.zoom));
    },

    resetZoom: () => {
      get().setZoom(1);
    },
  };
}

/** Zooms about the middle of the screen, so the work stays where the teacher is looking. */
function zoomCentred(viewport: Viewport, zoom: number): Viewport {
  return zoomAt(viewport, clampZoom(zoom), {
    x: viewport.widthPx / 2,
    y: viewport.heightPx / 2,
  });
}

export const useBoardStore = create<BoardState>()((set, get) => ({
  viewport: INITIAL_VIEWPORT,
  targetScreenWidth: DEFAULT_TARGET_WIDTH_PX,
  contentBounds: null,
  contentVersion: 0,
  background: DEFAULT_BACKGROUND,
  tool: 'pen',
  // SRS C4 lists stroke erasing first, and it is what a teacher reaches for most.
  eraserMode: 'stroke',
  eraserScreenWidth: DEFAULT_ERASER_PX,
  // SRS C4 names the lasso first, and it is what a diagram usually needs.
  selectMode: 'lasso',
  // SRS C4 lists the line first, and it is the shape a lesson reaches for most.
  shapeKind: 'line',
  polygonSides: DEFAULT_POLYGON_SIDES,
  selection: null,
  selectionCount: 0,
  selectionGrouped: false,
  selectionCopied: 0,
  fontSize: DEFAULT_FONT_SIZE,
  bold: false,
  italic: false,
  align: 'left',
  stickyColor: DEFAULT_STICKY_COLOR,
  activeLayer: 'content',
  pages: [],
  activePageId: null,
  editing: null,
  writing: null,
  snapUndo: null,
  // SRS C5 — on unless the teacher says otherwise.
  smartPen: true,
  snapConfidence: DEFAULT_SHAPE_CONFIDENCE,
  color: DEFAULT_PEN_COLOR,
  recentColors: [],
  // A board opened with no class behind it is the teacher's own scratch board.
  access: 'edit',

  ...viewportActions(set, get),
  ...selectionActions(set, get),
  ...penActions(set, get),
  ...textStyleActions(set, get),
  ...editingActions(set),
  ...contentActions(set, get),
}));
